import { __assign } from 'tslib';
import React, { useRef, useState, useEffect } from 'react';
import { isEmptyArray } from '../../atom/util/array.js';
import '../../foundation/color/index.js';
import '../../atom/util/dayjs.js';
import 'react-dom/client';
import Litepicker from 'litepicker';
import clsx from 'clsx';
import dayjs from 'dayjs';
import timeline from './plugin/timeline.js';
import useBaseProps from '../../atom/hook/useBaseProps.js';
import TextField from '../TextField/TextField.js';
import '../../tailwind.css.js';

var CalendarIcon = function (props) { return (React.createElement("svg", __assign({ width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, props),
    React.createElement("path", { d: "M15.2 4.00004V7.20004M8.80005 4.00004V7.20004M4.80005 10.4H19.2M6.40005 5.60004H17.6C18.4837 5.60004 19.2 6.31638 19.2 7.20004V18.4C19.2 19.2837 18.4837 20 17.6 20H6.40005C5.51639 20 4.80005 19.2837 4.80005 18.4V7.20004C4.80005 6.31638 5.51639 5.60004 6.40005 5.60004Z", stroke: "#2B303C", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }))); };
var ClearIcon = function (props) { return (React.createElement("svg", __assign({ width: "32", height: "32", viewBox: "0 0 32 32", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, props),
    React.createElement("path", { d: "M18.4 13.6L13.6 18.4M13.6 13.6L18.4 18.4M24 16C24 20.4183 20.4183 24 16 24C11.5817 24 8 20.4183 8 16C8 11.5817 11.5817 8 16 8C20.4183 8 24 11.5817 24 16Z", stroke: "#2B303C", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }))); };
var DatePicker = function (props) {
    var _a, _b;
    var baseProps = useBaseProps(props);
    var ref = useRef(null);
    var targetRef = useRef(null);
    var litepicker = useRef();
    var _c = useState(''), date = _c[0], setDate = _c[1];
    var format = props.dateFormat || 'D MMM, YYYY';
    var iconPos = props.iconPosition || 'left';
    var onSelected = function (startDate, endDate) {
        var _a, _b, _c, _d, _e, _f;
        var dates = [startDate, endDate].filter(function (dt) { return !!dt; });
        setDate(dates.map(function (dt) { return dayjs(dt).format(format); }).join(' - '));
        (_a = litepicker.current) === null || _a === void 0 ? void 0 : _a.clearSelection();
        (_b = litepicker.current) === null || _b === void 0 ? void 0 : _b.setStartDate(startDate);
        (_c = litepicker.current) === null || _c === void 0 ? void 0 : _c.setEndDate(endDate);
        (_d = litepicker.current) === null || _d === void 0 ? void 0 : _d.gotoDate(startDate, 0);
        (_e = litepicker.current) === null || _e === void 0 ? void 0 : _e.gotoDate(dayjs(startDate).month() - dayjs(endDate).month() === 0 ? dayjs(endDate).add(1, 'month').toDate() : endDate, 1);
        (_f = props.onChange) === null || _f === void 0 ? void 0 : _f.call(props, dates);
    };
    var handleSelection = function (startDate, endDate) {
        onSelected(startDate === null || startDate === void 0 ? void 0 : startDate.toJSDate(), endDate === null || endDate === void 0 ? void 0 : endDate.toJSDate());
    };
    var setup = function (picker) {
        if (!props.timeline) {
            picker.on('selected', handleSelection);
        }
    };
    var checkMinYear = function () {
        if (!props.minDate) {
            return props.minYear || 1990;
        }
        else {
            return new Date(props.minDate).getFullYear();
        }
    };
    var checkMaxYear = function () {
        if (!props.maxDate) {
            return props.maxYear || new Date().getFullYear();
        }
        else {
            return new Date(props.maxDate).getFullYear();
        }
    };
    var mount = function () {
        var _a, _b;
        if (ref.current != null) {
            var plugins = [];
            if (props.timeline) {
                Litepicker.add('timeline', timeline({
                    onSelected: onSelected,
                    timeline: props.timeline,
                    timelineRangeLabel: props.timelineRangeLabel,
                }));
                plugins.push('timeline');
            }
            var previousStartDate = ((_a = litepicker.current) === null || _a === void 0 ? void 0 : _a.getStartDate()) || undefined;
            var previousEndDate = ((_b = litepicker.current) === null || _b === void 0 ? void 0 : _b.getEndDate()) || undefined;
            litepicker.current = new Litepicker(__assign({ element: ref.current, parentEl: targetRef.current, startDate: previousStartDate, endDate: previousEndDate, autoApply: !props.range && !props.timeline, numberOfColumns: !props.range && !props.timeline ? 1 : 2, numberOfMonths: !props.range && !props.timeline ? 1 : 2, showWeekNumbers: true, singleMode: !props.range && !props.timeline, showTooltip: false, splitView: true, highlightedDays: props.highlightedDays || [], minDate: props.minDate ? props.minDate : undefined, maxDate: props.maxDate ? props.maxDate : undefined, dropdowns: {
                    minYear: checkMinYear(),
                    maxYear: checkMaxYear(),
                    months: true,
                    years: true,
                }, plugins: plugins, format: format, setup: setup }, props.configuration));
        }
        return unmount;
    };
    var unmount = function () {
        var _a;
        if (litepicker.current) {
            (_a = litepicker.current) === null || _a === void 0 ? void 0 : _a.destroy();
        }
    };
    useEffect(mount, [props.range, props.timeline, props.timelineRangeLabel, props.allowInput]);
    useEffect(function () {
        var _a, _b, _c, _d, _e;
        if (!props.value || isEmptyArray(props.value)) {
            if ((_a = litepicker.current) === null || _a === void 0 ? void 0 : _a.getDate()) {
                onSelected();
            }
        }
        else {
            var _f = props.value, start = _f[0], end = _f[1];
            if (start && start.getTime() !== ((_c = (_b = litepicker.current) === null || _b === void 0 ? void 0 : _b.getStartDate()) === null || _c === void 0 ? void 0 : _c.getTime()) || end && end.getTime() !== ((_e = (_d = litepicker.current) === null || _d === void 0 ? void 0 : _d.getEndDate()) === null || _e === void 0 ? void 0 : _e.getTime())) {
                onSelected(start, end);
            }
        }
    }, [props.value]);
    var handleDateChange = function (event) {
        var target = event.target;
        setDate(target.value);
    };
    var handleIconClick = function () {
        var _a, _b;
        (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus();
        (_b = ref.current) === null || _b === void 0 ? void 0 : _b.click();
    };
    var calendarIconButton = function () { return (React.createElement("button", { className: clsx({ 'focus:outline-error': props.error && !props.disabled, }), tabIndex: props.disabled ? -1 : undefined, onClick: handleIconClick },
        React.createElement(CalendarIcon, { "data-testid": 'info-icon', className: clsx('mb-[2px] mx-[5.33px]', { "cursor-pointer": !props.disabled, '[&>path]:stroke-disabled-text': props.disabled, '[&>path]:stroke-dark-text': !props.disabled }) }))); };
    var clearIconButton = function () { return (React.createElement("button", { className: clsx({ 'focus:outline-error': props.error && !props.disabled, }), tabIndex: props.disabled ? -1 : undefined, onClick: function () { return onSelected(); } },
        React.createElement(ClearIcon, { "data-testid": 'info-icon', className: clsx('mb-[2px] mx-[5.33px]', { "cursor-pointer": !props.disabled, '[&>path]:stroke-disabled-text': props.disabled, '[&>path]:stroke-dark-text': !props.disabled }) }))); };
    var inputPlaceholder = function () {
        if (props.placeholder) {
            return props.placeholder;
        }
        return props.range ? "".concat(format, " - ").concat(format) : "".concat(format);
    };
    return (React.createElement("div", { ref: targetRef, className: "relative [&>.litepicker]:!top-[42px] ".concat((props === null || props === void 0 ? void 0 : props.calendarPosition) === 'left'
            ? '[&>.litepicker]:!left-[0px]'
            : '[&>.litepicker]:!right-[0px]') }, props.allowInput ? (React.createElement("div", { className: clsx("relative", { "pointer-events-none": props.disabled }) },
        React.createElement(TextField, { width: "100%", required: props.required, error: props.error, helperMessage: props.helperMessage, disabled: props.disabled, before: props.clearable ? calendarIconButton() : null, after: React.createElement(React.Fragment, null,
                props.clearable && ((_a = litepicker.current) === null || _a === void 0 ? void 0 : _a.getDate()) && clearIconButton(),
                !props.clearable && calendarIconButton()), ref: ref, placeholder: inputPlaceholder(), value: date, onChange: handleDateChange, className: clsx('w-full disabled:outline-none', { 'bg-gray-205': props.disabled }) }),
        React.createElement("div", { className: 'bg-transparent !absolute top-0 right-0 mt-2 mr-1 flex items-center' }))) : (React.createElement(React.Fragment, null,
        React.createElement("button", __assign({}, baseProps, { type: 'button', name: props.name, disabled: props.disabled, ref: ref, className: clsx('flex items-center h-[40px] min-w-[268px] w-full border rounded-lg select-none', {
                'border-gray-204 focus:outline focus:outline-gray-204 enabled:hover:bg-blue-307': !props.error && !props.disabled,
                'border-error focus:outline-error': props.error,
                'cursor-pointer': !props.disabled,
                'px-4': props.allowInput,
                'pl-[9px] pr-[5px] pb-[2px]': !props.allowInput,
                'justify-between': iconPos === 'right',
                'bg-gray-205 border-gray-204 focus:border-transparent': props.disabled,
            }, props.className) }),
            React.createElement("div", { className: 'flex-1 flex items-center', onClick: handleIconClick },
                iconPos === 'left' && (React.createElement(CalendarIcon, { className: clsx('mr-1 pointer-events-none', {
                        '[&>path]:stroke-disabled-text': props.disabled,
                        '[&>path]:stroke-dark-text': !props.disabled,
                    }) })),
                React.createElement("span", { className: clsx('disabled:text-disabled-text pointer-events-none mt-[1px]', { 'ml-1': iconPos === 'right', 'text-disabled-text': props.disabled, 'text-dark-text': date && !props.disabled, 'text-light-text': !date && props.placeholder && !props.disabled }) }, date || props.placeholder)),
            props.clearable && ((_b = litepicker.current) === null || _b === void 0 ? void 0 : _b.getDate()) && (React.createElement(ClearIcon, { onClick: function () { return onSelected(); }, className: clsx('ml-2', {
                    '[&>path]:stroke-disabled-text': props.disabled,
                    '[&>path]:stroke-dark-text': !props.disabled,
                }) })),
            iconPos === 'right' && (React.createElement(CalendarIcon, { className: clsx('mr-1 pointer-events-none', {
                    '[&>path]:stroke-disabled-text': props.disabled,
                    '[&>path]:stroke-dark-text': !props.disabled,
                }) }))),
        React.createElement("span", { className: clsx('text-sm block h-fit mt-1 leading-4', {
                'text-disabled-text': props.disabled,
                'text-light-text': !props.error && !props.disabled,
                'text-error': props.error && !props.disabled,
            }) }, props.helperMessage)))));
};

export { DatePicker as default };
